import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import {
	contentControllerGetContentMetaDataByActivePage,
	type PdfActiveMetaDataPage,
	type PdfActiveMetaDataWord,
} from "@/shared/api/generated"
import { PDF_VIEWER_BUFFER_PAGES } from "./config"

interface UsePageMetaResult {
	getPageWords: (pageNumber: number) => PdfActiveMetaDataWord[]
	loading: boolean
	invalidate: () => void
}

const mapCombinedData = (
	data: Array<{
		word: string
		start_ts: number
		end_ts: number
		x: number
		y: number
		width: number
		height: number
		isAligned: boolean
		page: number
	}>,
	pageNum: number
): PdfActiveMetaDataWord[] =>
	data.map(w => ({
		word: w.word,
		start_ts: w.start_ts,
		end_ts: w.end_ts,
		chunkStartTs: w.start_ts,
		chunkEndTs: w.end_ts,
		chunkIdx: 0,
		x: w.x,
		y: w.y,
		width: w.width,
		height: w.height,
		pageIndex: pageNum,
		isAligned: w.isAligned,
	}))

/**
 * Loads word metadata for both audio-driven page AND scroll-visible page.
 * Merges results so user always sees overlays while scrolling.
 */
export const usePageMeta = (
	contentId: string,
	audioPage: number,
	scrollPage: number,
	combinedUrls?: string[]
): UsePageMetaResult => {
	const [pageMap, setPageMap] = useState<
		Map<number, PdfActiveMetaDataPage>
	>(new Map())
	const [loading, setLoading] = useState(false)
	const [fetchVersion, setFetchVersion] = useState(0)
	const loadedPagesRef = useRef<Set<number>>(new Set())
	/** Per-page abort controllers — only cancel requests for pages no longer needed */
	const inFlightRef = useRef<Map<number, AbortController>>(new Map())

	const isCompleted = !!combinedUrls?.length

	/** Pages within this radius of audioPage/scrollPage are kept; rest evicted */
	const KEEP_RADIUS = PDF_VIEWER_BUFFER_PAGES + 2

	const storePages = useCallback(
		(results: Map<number, PdfActiveMetaDataPage>) => {
			if (results.size === 0) return
			for (const k of results.keys()) loadedPagesRef.current.add(k)
			setPageMap(prev => {
				const merged = new Map(prev)
				for (const [k, v] of results) merged.set(k, v)
				return merged
			})
		},
		[]
	)

	useEffect(() => {
		const needed = new Set<number>()

		if (audioPage > 0) {
			for (let i = audioPage - PDF_VIEWER_BUFFER_PAGES; i <= audioPage + PDF_VIEWER_BUFFER_PAGES; i++) {
				if (i >= 1) needed.add(i)
			}
		}
		if (scrollPage > 0) {
			for (let i = scrollPage - PDF_VIEWER_BUFFER_PAGES; i <= scrollPage + PDF_VIEWER_BUFFER_PAGES; i++) {
				if (i >= 1) needed.add(i)
			}
		}

		// Evict pages far from both centers
		const keepSet = new Set<number>()
		if (audioPage > 0) {
			for (let i = audioPage - KEEP_RADIUS; i <= audioPage + KEEP_RADIUS; i++) {
				if (i >= 1) keepSet.add(i)
			}
		}
		if (scrollPage > 0) {
			for (let i = scrollPage - KEEP_RADIUS; i <= scrollPage + KEEP_RADIUS; i++) {
				if (i >= 1) keepSet.add(i)
			}
		}

		// Abort in-flight requests ONLY for pages that left the keep radius
		for (const [page, controller] of inFlightRef.current) {
			if (!keepSet.has(page)) {
				controller.abort()
				inFlightRef.current.delete(page)
			}
		}

		// Remove evicted pages from tracking and state
		for (const p of loadedPagesRef.current) {
			if (!keepSet.has(p)) loadedPagesRef.current.delete(p)
		}
		setPageMap(prev => {
			let evicted = false
			for (const k of prev.keys()) {
				if (!keepSet.has(k)) {
					evicted = true
					break
				}
			}
			if (!evicted) return prev
			const cleaned = new Map<number, PdfActiveMetaDataPage>()
			for (const [k, v] of prev) {
				if (keepSet.has(k)) cleaned.set(k, v)
			}
			return cleaned
		})

		// Filter out pages already loaded or already in-flight
		const toLoad = (
			fetchVersion > 0
				? [...needed]
				: [...needed].filter(p => !loadedPagesRef.current.has(p))
		).filter(p => !inFlightRef.current.has(p))

		if (toLoad.length === 0) return

		setLoading(true)

		if (isCompleted) {
			// Completed mode: each page fetched independently in parallel
			for (const pageNum of toLoad) {
				if (!combinedUrls || pageNum > combinedUrls.length) continue
				const url = combinedUrls[pageNum - 1]
				if (!url) continue

				const controller = new AbortController()
				inFlightRef.current.set(pageNum, controller)

				axios
					.get(url, {
						signal: controller.signal,
						headers: { "Cache-Control": "public, max-age=31536000" },
					})
					.then(response => {
						const words = mapCombinedData(response.data, pageNum)
						storePages(
							new Map([[pageNum, { words, pageIndex: pageNum }]])
						)
					})
					.catch(e => {
						if (e instanceof DOMException && e.name === "AbortError") return
						console.error("Failed to fetch page meta:", e)
					})
					.finally(() => {
						inFlightRef.current.delete(pageNum)
					})
			}
		} else {
			// Processing mode: each center page fetched independently in parallel
			for (const center of toLoad) {
				const controller = new AbortController()
				inFlightRef.current.set(center, controller)

				contentControllerGetContentMetaDataByActivePage(
					contentId,
					{ pageIndex: String(center) },
					{ signal: controller.signal }
				)
					.then(response => {
						// This hook only serves the PDF viewer; the endpoint's
						// response is discriminated by viewMode.
						if (response.viewMode !== "PDF") return
						const results = new Map<number, PdfActiveMetaDataPage>()
						if (response.prevPageWords?.words?.length) {
							results.set(response.prevPageWords.pageIndex, response.prevPageWords)
						}
						if (response.currentPageWords?.words?.length) {
							results.set(response.currentPageWords.pageIndex, response.currentPageWords)
						}
						if (response.nextPageWords?.words?.length) {
							results.set(response.nextPageWords.pageIndex, response.nextPageWords)
						}
						storePages(results)
					})
					.catch(e => {
						if (e instanceof DOMException && e.name === "AbortError") return
						console.error("Failed to fetch page meta:", e)
					})
					.finally(() => {
						inFlightRef.current.delete(center)
					})
			}
		}

		setLoading(false)
	}, [
		contentId,
		audioPage,
		scrollPage,
		fetchVersion,
		isCompleted,
		combinedUrls,
		storePages,
	])

	// Cleanup all in-flight on unmount
	useEffect(() => {
		return () => {
			for (const controller of inFlightRef.current.values()) {
				controller.abort()
			}
			inFlightRef.current.clear()
		}
	}, [])

	const invalidate = useCallback(() => {
		loadedPagesRef.current.clear()
		setFetchVersion(v => v + 1)
	}, [])

	const getPageWords = useCallback(
		(pageNumber: number): PdfActiveMetaDataWord[] => {
			return pageMap.get(pageNumber)?.words ?? []
		},
		[pageMap]
	)

	return { getPageWords, loading, invalidate }
}
